import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { MdDescription, MdMenuBook, MdQuiz, MdVideoLibrary } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import { getAllSectionsRequest } from "../../../modules/students-modules/sections/Actions";

const C = {
  primary: "#7A1422",
  secondary: "#8B1828",
  border: "#D6CEC0",
  muted: "rgba(139, 24, 40, 0.55)",
};

function publicImageUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  const raw = process.env.REACT_APP_API_URL || "https://api.edutopiaonline.com/";
  const base = String(raw).replace(/\s/g, "").replace(/['"]/g, "").replace(/\/$/, "");
  const clean = String(path).replace(/^\//, "");
  return `${base}/public/${clean}`;
}

const StatCell = ({ icon: Icon, label, value }) => (
  <Flex align="flex-start" gap={2}>
    <Box color={C.secondary} mt="2px" flexShrink={0}>
      <Icon size={18} />
    </Box>
    <Box minW={0}>
      <Text fontSize="9px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.05em" lineHeight="1.2">
        {label}
      </Text>
      <Text fontSize="xs" fontWeight="700" color={C.secondary} lineHeight="1.25" mt={0.5}>
        {value}
      </Text>
    </Box>
  </Flex>
);

const CourseCard = ({ section }) => {
  const img = publicImageUrl(section.image);
  const lessons = section.lessons_count ?? 0;
  const videos = section.videos_count ?? 0;
  const pdfs = section.pdfs_count ?? 0;
  const exams = section.exams_count ?? 0;
  const progress = 0;

  return (
    <Box
      w="100%"
      maxW="280px"
      mx="auto"
      bg="white"
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
      willChange="transform"
      _hover={{
        borderColor: C.primary,
        transform: "scale(1.03)",
        boxShadow: "0 18px 46px rgba(122, 20, 34, 0.22), 0 0 0 1px rgba(214, 206, 192, 0.45)",
      }}
      _active={{ transform: "scale(1.01)" }}
    >
      <Box h={{ base: "120px", sm: "132px" }} w="100%" overflow="hidden" bg="gray.100" flexShrink={0}>
        {img ? (
          <Image
            src={img}
            alt={section.name || "Course"}
            w="100%"
            h="100%"
            objectFit="cover"
            fallback={<Skeleton h="full" w="full" />}
          />
        ) : (
          <Flex h="full" align="center" justify="center" bg="rgba(122, 20, 34, 0.08)" color={C.primary} fontWeight="800">
            {section.name?.slice(0, 2)?.toUpperCase() || "—"}
          </Flex>
        )}
      </Box>
      <Box p={4} flex={1} display="flex" flexDirection="column">
        <Heading
          as="h3"
          fontSize="md"
          fontWeight="800"
          color={C.primary}
          fontFamily="'Tajawal','Amiri',sans-serif"
          mb={3}
          lineHeight="1.3"
          noOfLines={2}
        >
          {section.name}
        </Heading>

        <SimpleGrid columns={2} spacingY={2.5} spacingX={2} mb={4} borderLeftWidth="2px" borderColor={C.border} pl={3}>
          <StatCell icon={MdMenuBook} label="Lessons" value={`${lessons} lesson${lessons === 1 ? "" : "s"}`} />
          <StatCell icon={MdVideoLibrary} label="Videos" value={`${videos}`} />
          <StatCell icon={MdDescription} label="PDFs" value={`${pdfs} file${pdfs === 1 ? "" : "s"}`} />
          <StatCell icon={MdQuiz} label="Exams" value={`${exams} exam${exams === 1 ? "" : "s"}`} />
        </SimpleGrid>

        <Box mt="auto">
          <Flex justify="space-between" align="center" mb={1.5}>
            <Text fontSize="9px" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
              Course progress
            </Text>
            <Text fontSize="xs" fontWeight="800" color={C.primary}>
              {progress}%
            </Text>
          </Flex>
          <Box h="5px" w="100%" bg={C.border} borderRadius="full" overflow="hidden">
            <Box h="full" w={`${progress}%`} bg={C.primary} borderRadius="full" transition="width 0.3s" />
          </Box>
          <Button
            as={Link}
            to={`/students/courses/${section.id}`}
            w="full"
            mt={4}
            py={3}
            size="sm"
            fontSize="sm"
            borderWidth="2px"
            borderColor={C.primary}
            color={C.primary}
            fontWeight="800"
            borderRadius="lg"
            variant="outline"
            _hover={{ bg: C.secondary, color: "#FAF2EB", borderColor: C.secondary }}
          >
            Continue learning
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const StudentCourses = () => {
  const dispatch = useDispatch();
  const sections = useSelector((state) => state.sections.sections);
  const isLoading = useSelector((state) => state.sections.isLoading);
  const userData = useSelector((state) => state.user.userData);
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("all");

  useEffect(() => {
    dispatch(getAllSectionsRequest());
  }, [dispatch]);

  const filtered = useMemo(() => {
    // Reducer stores the array on `sections`; API may also appear as `{ data: [...] }` if shape changes
    const raw = sections;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

    const subjectId = userData?.subject?.id;
    const bySubject = list.filter((s) => {
      if (subjectId == null || subjectId === undefined) return true;
      if (s.subject_id == null || s.subject_id === undefined) return true;
      return Number(s.subject_id) === Number(subjectId);
    });

    const q = search.trim().toLowerCase();
    if (!q) return bySubject;
    return bySubject.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [sections, userData?.subject?.id, search]);

  return (
    <StudentPortalShell>
      <Flex direction="column" minH={{ md: "100vh" }}>
        <Box
          as="header"
          position="sticky"
          top={{ base: 0, md: 0 }}
          zIndex={20}
          bg="#E9E2DB"
          borderBottomWidth="1px"
          borderColor={C.border}
          px={{ base: 4, md: 8 }}
          py={4}
          display={{ base: "none", md: "flex" }}
          align="center"
          justify="space-between"
          gap={4}
        >
          <Text fontSize="lg" fontWeight="900" color={C.primary} fontFamily="'Tajawal','Amiri',sans-serif">
            Edutopia Online
          </Text>
          <InputGroup maxW="md" flex="1">
            <InputLeftElement pointerEvents="none" h="full">
              <FiSearch color="rgba(106, 30, 30, 0.45)" />
            </InputLeftElement>
            <Input
              placeholder="Search curriculum…"
              bg="rgba(255,255,255,0.6)"
              borderColor={C.border}
              borderRadius="md"
              pl={10}
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              _focus={{ borderColor: C.primary, boxShadow: "0 0 0 1px rgba(106, 30, 30, 0.25)" }}
            />
          </InputGroup>
          <Box w="32px" flexShrink={0} />
        </Box>

        <Box flex="1" p={{ base: 4, md: 8 }} pt={{ base: 4, md: 8 }}>
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            gap={6}
            mb={10}
          >
            <Box>
              <Heading
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={C.primary}
                fontFamily="'Tajawal','Amiri',sans-serif"
                letterSpacing="tight"
              >
                Active curriculum
              </Heading>
              <Text mt={2} color={C.muted} fontSize="sm" maxW="lg">
                Browse and continue your academic courses.
              </Text>
            </Box>
          </Flex>

          <InputGroup display={{ base: "flex", md: "none" }} mb={6}>
            <InputLeftElement pointerEvents="none" h="full">
              <FiSearch color="rgba(106, 30, 30, 0.45)" />
            </InputLeftElement>
            <Input
              placeholder="Search curriculum…"
              bg="rgba(255,255,255,0.85)"
              borderColor={C.border}
              borderRadius="full"
              pl={10}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          {isLoading ? (
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }}
              gap={{ base: 6, md: 8 }}
              justifyItems="center"
            >
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} h="320px" w="100%" maxW="280px" borderRadius="xl" />
              ))}
            </Grid>
          ) : filtered.length === 0 ? (
            <Text color={C.muted} fontWeight="600">
              No courses match your filters yet.
            </Text>
          ) : (
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }}
              gap={{ base: 6, md: 8 }}
              justifyItems="center"
            >
              {filtered.map((section) => (
                <CourseCard key={section.id} section={section} />
              ))}
            </Grid>
          )}
        </Box>
      </Flex>
    </StudentPortalShell>
  );
};

export default StudentCourses;
